/**
 * Types for the Source-with-Agent stream. Mirror the backend SSE contract in
 * sourcing_agent/agent/runner.py.
 *
 * Search now returns external PREVIEWS (no score): the agent surfaces who it
 * found, and the recruiter "unlocks" a profile (a paid People Lookup) to reveal
 * the full record and add it to the talent pool. Scoring happens later, when the
 * unlocked talent is assigned to a job through the existing pool flow.
 */

export interface AgentCriteria {
  job_title?: string;
  location?: string;
  years_of_experience?: number;
  skills: string[];
  industry?: string;
}

export interface AgentChip {
  kind: 'location' | 'radius' | 'yoe' | 'skill' | 'industry' | string;
  label: string;
}

export interface CriteriaItem {
  weight: number;
  text: string;
}

/** One presented search "angle". The run is shown as several sequential steps
 *  even though there is a single API call underneath. */
export interface AgentSearchStep {
  index: number;
  label: string;
  status: 'running' | 'done';
}

/** Debug frame: every real attempt (query sent + raw count). The agent broadens
 *  once if the first attempt is thin, so there can be 1-2 attempts. Rendered only
 *  in dev builds. */
export interface AgentDebugAttempt {
  label: string;
  query: unknown;
  count: number;
}

export interface AgentDebug {
  attempts: AgentDebugAttempt[];
  count: number;
}

/** Per-candidate unlock lifecycle, tracked client-side. */
export type UnlockStatus = 'locked' | 'unlocking' | 'unlocked' | 'error';

export interface CandidateUnlock {
  status: UnlockStatus;
  /** Tenant TalentPoolProfile id created by the unlock. */
  profileId?: string;
  /** Whether the lookup was served from the platform cache. */
  cached?: boolean;
  /** Localized-message code when status === 'error'. */
  error?: string;
}

/** One source-agnostic search preview. ``dedupKey`` is the stable identity used
 *  to unlock / cache. No score: previews carry no skills/education. */
export interface AgentCandidate {
  source: string;
  sourceId: string;
  dedupKey: string;
  fullName?: string | null;
  title?: string | null;
  employer?: string | null;
  location?: string | null;
  initials: string;
  linkedinUrl?: string | null;
  profileUrl?: string | null;
  avatarUrl?: string | null;
  /** Added client-side; absent in the raw stream payload. */
  unlock?: CandidateUnlock;
}

export type AgentPhase = 'idle' | 'running' | 'done' | 'error';

/** Accumulated view of the stream, rebuilt as events arrive. */
export interface AgentRunState {
  phase: AgentPhase;
  need?: { title: string; chips: AgentChip[] };
  analysis?: string;
  criteria?: { text: string; items: CriteriaItem[] };
  searchReasoning?: string;
  steps: AgentSearchStep[];
  resultCount?: number;
  debug?: AgentDebug;
  candidates: AgentCandidate[];
  error?: string;
}

export const EMPTY_RUN: AgentRunState = {
  phase: 'idle',
  steps: [],
  candidates: [],
};
